import { memo, useRef, useEffect, useState } from 'react'
import { motion, type Variants } from 'motion/react'
import { clamp } from './clamp'

interface IMaxHeightContainer {
  data: string
  elipsis: string
  force_link?: boolean
  className?: string
}

export const MaxHeightContainer = memo<IMaxHeightContainer>( ({ className = '', data, elipsis, force_link = false }) => {
  const elementRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const [isExpanded, setExpanded] = useState(false)

  const handleExpanded = () => {
    if (!buttonRef.current) return
    buttonRef.current.remove()
    setExpanded(true)
  }

  const handleHover = (isHover: boolean) => () => {
    if (buttonRef.current && !force_link) {
      buttonRef.current.classList.toggle('active', isHover)
    }
  }

  useEffect(() => {
    if (!elementRef.current) return

    // Run clamp after render
    const frameId = requestAnimationFrame(() => {
      const currentElement = elementRef.current
      if (!currentElement) return

      const isClamped = clamp(currentElement, currentElement.offsetHeight, { ellipsis: elipsis })
      const currentButton = currentElement.getElementsByClassName('dy-max-button')[0] as HTMLButtonElement | null
      buttonRef.current = currentButton

      if (force_link && currentButton) {
        currentButton.addEventListener('click', handleExpanded)
      }

      setExpanded(!isClamped)
    })

    return () => {
      cancelAnimationFrame(frameId)
      if (force_link && buttonRef.current) {
        buttonRef.current.removeEventListener('click', handleExpanded)
      }
    }
  }, [data, elipsis, force_link])

  return isExpanded ? (
    <motion.div  animate='active'  variants={variants} className={`dy-max expanded ${className}`} dangerouslySetInnerHTML={{ __html: data }} />) : (
    <div ref={elementRef}  className={`dy-max ${className}`} dangerouslySetInnerHTML={{ __html: data }} onClick={handleExpanded} onMouseEnter={handleHover(true)}  onMouseLeave={handleHover(false)}  />
  )
}
)

const variants:Variants = {
  active: {
    height: 'auto',
    transition: {
      type: 'tween',
      ease: [0.25, 0.1, 0.25, 1],
      duration: 0.3,
    },
  },
  init: {
    height: '0px',
    transition: {
      type: 'tween',
      ease: [0.25, 0.1, 0.25, 1],
      duration: 0.3,
    },
  },
}
